import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

/**
 * CIF de la empresa que se está viendo.
 *
 * Vive en redux, y no como prop ni en el estado de `EmpresaDetalle`, porque quien lo
 * necesita es `useIntegraWeb` (`pages/Empresas/controllers/services/noticias.services.ts`),
 * el fetcher compartido contra el WS d'Activitats: los servicios se instancian con
 * `NoticiasServices.GET_NOTICIAS()` y compañía, sin argumentos, así que no hay por
 * dónde pasarles el CIF sin reescribir todas las llamadas.
 *
 * Lo rellena `EmpresaDetalle` cuando carga el detalle y lo deja a '' al salir o si
 * la carga falla, para que un CIF de la empresa anterior no se quede pegado.
 */
export interface EmpresaState {
  cif: string
  /**
   * Indica si el usuario de la sesión tiene alguna empresa con acceso autorizado.
   *
   * Lo consulta `AppRouter` al arrancar (`GET /integra-web/empresas/mi-acceso`). Con él el
   * menú decide si muestra el acceso al módulo de empresas y `AccesoEmpresasRoute` si deja
   * entrar en `/empreses`. No se guarda en `localStorage` a propósito: es un permiso, y un
   * valor cacheado de una sesión anterior podría enseñarle el enlace a quien ya no tiene
   * acceso.
   *
   * Arranca en `null` = todavía no consultado, que NO es lo mismo que `false`. El guard
   * necesita distinguirlos: con un `false` inicial, entrar directo a `/empreses` en un F5
   * redirigiría a home antes de que llegara la respuesta y nadie podría entrar nunca.
   */
  tieneEmpresasAutorizadas: boolean | null
  /**
   * Contador que se incrementa cada vez que se envía una solicitud de acceso o un alta de
   * empresa. Lo consume `ListadoEmpresas` (a través de `pages/Empresas`) para recargarse.
   *
   * Vive en redux y no como estado de `pages/Empresas` porque el disparador de la solicitud
   * también está en la cabecera (`Navtop`), que es otro árbol de React: con un `useState`
   * local, pedir acceso desde la cabecera estando dentro del módulo no refrescaría el listado.
   */
  solicitudesRefreshKey: number
}

const initialState: EmpresaState = {
  cif: '',
  tieneEmpresasAutorizadas: null,
  solicitudesRefreshKey: 0,
}

export const empresaSlice = createSlice({
  name: 'empresa',
  initialState,
  reducers: {
    setEmpresaCif: (state, action: PayloadAction<string>) => {
      state.cif = action.payload || ''
    },
    setTieneEmpresasAutorizadas: (state, action: PayloadAction<boolean | null>) => {
      state.tieneEmpresasAutorizadas = action.payload
    },
    bumpSolicitudesRefreshKey: (state) => {
      state.solicitudesRefreshKey += 1
    }
  },
})

export const {
  setEmpresaCif,
  setTieneEmpresasAutorizadas,
  bumpSolicitudesRefreshKey
} = empresaSlice.actions
